import React, { useState } from 'react';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import { FiMapPin, FiPhone, FiMail, FiClock, FiSend } from 'react-icons/fi';

const ContactContainer = styled.div`
  padding-top: 80px;
`;

const HeroSection = styled.section`
  height: 40vh;
  background: var(--dark-bg);
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: white;
  position: relative;
  overflow: hidden;
`;

const HeroContent = styled(motion.div)`
  h1 {
    font-size: 4rem;
    font-weight: 700;
    margin-bottom: 1rem;
    color: white;

    span {
      background: linear-gradient(135deg, #d395ff, #ff7fd9);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
    }

    @media (max-width: 768px) {
      font-size: 2.5rem;
    }
  }

  p {
    color: rgba(255, 255, 255, 0.8);
    font-size: 1.1rem;
  }
`;

const ContactContent = styled.div`
  max-width: 1400px;
  margin: -50px auto 0;
  padding: 0 5% 4rem;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  position: relative;
  z-index: 10;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
  }
`;

const InfoSection = styled(motion.div)`
  background: white;
  border-radius: 20px;
  padding: 3rem;
  box-shadow: var(--shadow-lg);
`;

const SectionTitle = styled.h2`
  font-size: 2rem;
  color: var(--text-dark);
  margin-bottom: 2rem;

  span {
    display: block;
    font-size: 1rem;
    color: var(--primary-color);
    text-transform: uppercase;
    letter-spacing: 2px;
    margin-bottom: 0.5rem;
  }
`;

const InfoGrid = styled.div`
  display: grid;
  gap: 2rem;
  margin-bottom: 3rem;
`;

const InfoCard = styled(motion.div)`
  display: flex;
  gap: 1.5rem;
  padding: 1.5rem;
  background: #f8f8f8;
  border-radius: 16px;
  transition: var(--transition);

  &:hover {
    background: linear-gradient(135deg, #f8f0ff, #fff0f8);
  }

  svg {
    font-size: 1.8rem;
    color: var(--primary-color);
    flex-shrink: 0;
  }

  h3 {
    font-size: 1.2rem;
    color: var(--text-dark);
    margin-bottom: 0.5rem;
  }

  p {
    color: var(--text-gray);
    line-height: 1.6;
  }
`;

const MapContainer = styled.div`
  height: 300px;
  background: #f0f0f0;
  border-radius: 16px;
  overflow: hidden;
  position: relative;

  iframe {
    width: 100%;
    height: 100%;
    border: none;
  }
`;

const FormSection = styled(motion.div)`
  background: white;
  border-radius: 20px;
  padding: 3rem;
  box-shadow: var(--shadow-lg);
`;

const Form = styled.form`
  display: grid;
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  display: grid;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-size: 0.95rem;
  font-weight: 500;
  color: var(--text-dark);
`;

const Input = styled.input`
  padding: 1rem;
  border: 1px solid var(--border-light);
  border-radius: 8px;
  font-size: 1rem;
  transition: var(--transition);

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(211,149,255,0.1);
  }
`;

const TextArea = styled.textarea`
  padding: 1rem;
  border: 1px solid var(--border-light);
  border-radius: 8px;
  font-size: 1rem;
  min-height: 150px;
  resize: vertical;
  transition: var(--transition);

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(211,149,255,0.1);
  }
`;

const SubmitButton = styled(motion.button)`
  padding: 1rem 2rem;
  background: linear-gradient(135deg, #d395ff, #ff7fd9);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  transition: var(--transition);

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  svg {
    font-size: 1.2rem;
  }
`;

const SuccessMessage = styled(motion.div)`
  padding: 1rem;
  background: #b0ff64;
  color: #0a0a0a;
  border-radius: 8px;
  text-align: center;
  font-weight: 500;
`;

const FAQSection = styled.section`
  padding: 4rem 10%;
  background: #f8f8f8;
`;

const FAQGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;
  max-width: 1200px;
  margin: 3rem auto 0;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FAQItem = styled(motion.div)`
  background: white;
  padding: 2rem;
  border-radius: 16px;
  box-shadow: var(--shadow-sm);

  h3 {
    font-size: 1.2rem;
    color: var(--text-dark);
    margin-bottom: 1rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;

    &::before {
      content: 'Q:';
      color: var(--primary-color);
      font-weight: 700;
    }
  }

  p {
    color: var(--text-gray);
    line-height: 1.6;
    padding-left: 1.5rem;
  }
`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    console.log('Form submitted:', formData);
    setSubmitted(true);
    setLoading(false);
    setFormData({ name: '', email: '', subject: '', message: '' });
    
    setTimeout(() => setSubmitted(false), 5000);
  };

  const faqs = [
    {
      question: 'How long does shipping take?',
      answer: 'Domestic orders typically arrive within 3-5 business days. International shipping can take 7-14 business days depending on the destination.'
    },
    {
      question: 'Do you offer wholesale pricing?',
      answer: 'Yes! We offer special pricing for bulk orders. Please contact our sales team for a custom quote.'
    },
    {
      question: 'Are your candies gluten-free?',
      answer: 'Many of our candies are gluten-free. Check individual product descriptions or contact us for specific dietary requirements.'
    },
    {
      question: 'What is your return policy?',
      answer: 'We want you to be completely satisfied. If youre not happy with your purchase, contact us within 30 days for a full refund.'
    }
  ];

  return (
    <ContactContainer>
      <HeroSection>
        <HeroContent
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h1>
            Get in <span>Touch</span>
          </h1>
          <p>We'd love to hear from you</p>
        </HeroContent>
      </HeroSection>

      <ContactContent>
        <InfoSection
          initial={{ opacity: 0, x: -30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
        >
          <SectionTitle>
            <span>CONTACT INFO</span>
            Let's Connect
          </SectionTitle>

          <InfoGrid>
            <InfoCard whileHover={{ x: 5 }}>
              <FiMapPin />
              <div>
                <h3>Visit Us</h3>
                <p>B-5<br />GIRWAI<br />Gwalior, Madhya Pradesh</p>
              </div>
            </InfoCard>

            <InfoCard whileHover={{ x: 5 }}>
              <FiPhone />
              <div>
                <h3>Call Us</h3>
                <p>+91 9009206707<br />Mon-Fri, 9am-6pm EST</p>
              </div>
            </InfoCard>

            <InfoCard whileHover={{ x: 5 }}>
              <FiMail />
              <div>
                <h3>Email Us</h3>
                <p>hello@kemlo.com<br />support@kemlo.com</p>
              </div>
            </InfoCard>

            <InfoCard whileHover={{ x: 5 }}>
              <FiClock />
              <div>
                <h3>Store Hours</h3>
                <p>Monday - Friday: 9am - 8pm<br />Saturday: 10am - 6pm<br />Sunday: 11am - 5pm</p>
              </div>
            </InfoCard>
          </InfoGrid>

          <MapContainer>
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m14!1m12!1m3!1d1154.9809086938067!2d78.11236816609521!3d26.18612779740217!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!5e1!3m2!1sen!2sin!4v1772826976039!5m2!1sen!2sin"
              allowFullScreen=""
              loading="lazy"
              title="Kemlo Location"
            />
          </MapContainer>
        </InfoSection>

        <FormSection
          initial={{ opacity: 0, x: 30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
        >
          <SectionTitle>
            <span>SEND MESSAGE</span>
            Drop Us a Line
          </SectionTitle>

          {submitted && (
            <SuccessMessage
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
            >
              Thank you for reaching out! We'll get back to you within 24 hours.
            </SuccessMessage>
          )}

          <Form onSubmit={handleSubmit}>
            <FormGroup>
              <Label>Your Name</Label>
              <Input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                placeholder="John Doe"
              />
            </FormGroup>

            <FormGroup>
              <Label>Email Address</Label>
              <Input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                placeholder="john@example.com"
              />
            </FormGroup>

            <FormGroup>
              <Label>Subject</Label>
              <Input
                type="text"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                required
                placeholder="What's this about?"
              />
            </FormGroup>

            <FormGroup>
              <Label>Message</Label>
              <TextArea
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                placeholder="Tell us how we can help..."
              />
            </FormGroup>

            <SubmitButton
              type="submit"
              disabled={loading}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {loading ? 'Sending...' : (
                <>
                  Send Message <FiSend />
                </>
              )}
            </SubmitButton>
          </Form>
        </FormSection>
      </ContactContent>

      <FAQSection>
        <SectionTitle style={{ textAlign: 'center' }}>
          <span>FAQ</span>
          Frequently Asked Questions
        </SectionTitle>

        <FAQGrid>
          {faqs.map((faq, index) => (
            <FAQItem
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              whileHover={{ y: -5 }}
            >
              <h3>{faq.question}</h3>
              <p>{faq.answer}</p>
            </FAQItem>
          ))}
        </FAQGrid>
      </FAQSection>
    </ContactContainer>
  );
};

export default Contact;